/**
 * Cart Helper Functions
 * Ported from legacy nv-nav-cart-build (lines 2328-2582)
 */

// Extend Number prototype for price formatting
declare global {
  interface Number {
    formatPriceStr(precision?: number, decimal?: string, thousands?: string): string;
    formatPrice(precision?: number, decimal?: string, thousands?: string): string;
  }
  
  interface Window {
    site?: any;
    nvCartHelpers?: any;
    locale?: string;
  }
}

/**
 * Get total price of all items in cart
 */
const getCartTotal = function (cart: any[]): string {
  let cartTotal = 0;
  if (cart && cart.length > 0) {
    cartTotal = cart.reduce(function (total, product) {
      return total + (parseFloat(product.unitPrice) * parseFloat(product.quantity));
    }, 0);
  }
  return setPriceFormatToLocale(cartTotal);
};

/**
 * Get cart total as formatted string with currency symbol
 */
const getCartTotalStr = function (cart: any[]): string {
  return "$" + parseFloat(getCartTotal(cart)).formatPriceStr();
};

/**
 * Get quantity count for a specific product in cart
 */
const getProductCount = function (cart: any[], productId: string): number {
  return cart.filter(function (product) {
    return product.id == productId;
  }).reduce(function (total, product) {
    return total + product.quantity;
  }, 0);
};

/**
 * Get limit per customer for a product
 */
const getLimitPerCustomer = function (cart: any[], productId: string): number {
  return cart.filter(function (product) {
    return product.id == productId;
  }).reduce(function (total, product) {
    return total + product.limitPerCustomer;
  }, 0);
};

/**
 * Get available quantity limit for a product
 */
const getAvailableLimit = function (cart: any[], productId: string): number {
  return cart
    .filter(function (product) {
      return product.id == productId;
    })
    .reduce(function (total, product) {
      return product.availableQuantity;
    }, 0);
};

/**
 * Get total count of all products in cart
 */
const getTotalProductCount = function (cart: any[]): number {
  if (cart && cart.length > 0) {
    return cart.reduce(function (total, product) {
      return total + product.quantity;
    }, 0);
  } else {
    return 0;
  }
};

/**
 * Calculate total price of cart products
 */
const productCartProductPrice = function (cart: any[]): string {
  let cartProductTotal = 0;
  cartProductTotal = cart.reduce(function (total, product) {
    return total + product.totalPrice;
  }, 0);
  return setPriceFormatToLocale(cartProductTotal);
};

/**
 * Check if product is in cart
 */
const productIsInCart = function (cart: any[], productId: string): boolean {
  return getProductCount(cart, productId) > 0;
};

/**
 * Format price according to locale
 */
const setPriceFormatToLocale = function (price: number): string {
  return parseFloat(String(price)).formatPrice();
};

/**
 * Format price as string (Number prototype extension)
 */
Number.prototype.formatPriceStr = function (
  precision?: number,
  decimal?: string,
  thousands?: string
): string {
  const num = this.valueOf();
  const locale = (typeof window !== 'undefined' && window.locale) || 'en-us';
  
  let precisionValue: number;
  let decimalValue: string;
  let thousandsValue: string;

  switch (locale.toLowerCase()) {
    case "en":
    case "en-zz":
    case "en-ca":
    case "en-us":
    case "en-gb":
    case "en-eu":
    case "en-in":
    case "en-au":
    default:
      precisionValue = isNaN((precision = Math.abs(precision!))) ? 2 : precision;
      decimalValue = decimal === undefined ? "." : decimal;
      thousandsValue = thousands === undefined ? "," : thousands;
      break;
    case "ru-ru":
    case "sv-se":
      precisionValue = isNaN((precision = Math.abs(precision!))) ? 0 : precision;
      decimalValue = decimal === undefined ? "" : decimal;
      thousandsValue = thousands === undefined ? " " : thousands;
      break;
    case "fr-fr":
    case "de-de":
    case "es-es":
    case "it-it":
    case "pl-pl":
    case "nb-no":
    case "da-dk":
    case "fr-be":
    case "de-at":
    case "cs-cz":
    case "fi-fi":
    case "nl-nl":
      precisionValue = isNaN((precision = Math.abs(precision!))) ? 2 : precision;
      decimalValue = decimal === undefined ? "," : decimal;
      thousandsValue = thousands === undefined ? "." : thousands;
      break;
  }

  const s = num < 0 ? "-" : "";
  const i = parseInt((Math.abs(+num || 0).toFixed(precisionValue))) + "";
  const j = i.length > 3 ? i.length % 3 : 0;
  
  return (
    s +
    (j ? i.substr(0, j) + thousandsValue : "") +
    i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousandsValue) +
    (precisionValue
      ? decimalValue +
        "" +
        Math.abs(num - parseInt(i))
          .toFixed(precisionValue)
          .slice(2)
      : "")
  );
};

/**
 * Format price (Number prototype extension)
 */
Number.prototype.formatPrice = function (
  precision?: number,
  decimal?: string,
  thousands?: string
): string {
  const num = this.valueOf();
  const locale = (typeof window !== 'undefined' && window.locale) || 'en-us';
  
  let precisionValue: number;
  let decimalValue: string;
  let thousandsValue: string;

  switch (locale.toLowerCase()) {
    case "en":
    case "en-zz":
    case "en-ca":
    case "en-us":
    case "en-gb":
    case "en-eu":
    case "en-in":
    case "en-au":
    default:
      precisionValue = isNaN((precision = Math.abs(precision!))) ? 2 : precision;
      decimalValue = decimal === undefined ? "." : decimal;
      thousandsValue = thousands === undefined ? "," : thousands;
      break;
    case "ru-ru":
    case "sv-se":
      precisionValue = isNaN((precision = Math.abs(precision!))) ? 0 : precision;
      decimalValue = decimal === undefined ? "" : decimal;
      thousandsValue = thousands === undefined ? " " : thousands;
      break;
    case "fr-fr":
    case "de-de":
    case "es-es":
    case "it-it":
    case "pl-pl":
    case "nb-no":
    case "da-dk":
    case "fr-be":
    case "de-at":
    case "cs-cz":
    case "fi-fi":
    case "nl-nl":
      precisionValue = isNaN((precision = Math.abs(precision!))) ? 2 : precision;
      decimalValue = decimal === undefined ? "," : decimal;
      thousandsValue = thousands === undefined ? "." : thousands;
      break;
  }

  const s = num < 0 ? "-" : "";
  const i = parseInt((Math.abs(+num || 0).toFixed(precisionValue))) + "";
  const j = i.length > 3 ? i.length % 3 : 0;
  
  return (
    s +
    (j ? i.substr(0, j) + thousandsValue : "") +
    i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousandsValue) +
    (precisionValue
      ? decimalValue +
        "" +
        Math.abs(num - parseInt(i))
          .toFixed(precisionValue)
          .slice(2)
      : "")
  );
};

/**
 * Display price with localized currency symbol
 * Ensures decimal precision (e.g. $0.00) by formatting the numeric value before adding the currency symbol.
 */
const localisePriceDisplay = function (apiCurrency: string, product_price: any): string {
  const dispCurrencyMap: { [key: string]: string } = {
    EUR: "€",
    GBP: "£",
    PLN: "zł",
    NOK: "kr",
    SEK: "kr",
    DKK: "kr",
    CZK: "Kč",
    USD: "$",
    AUD: "A$",
    JPY: "JPY",
    CNY: "CNY",
    KRW: "KRW",
    TWD: "TWD",
    INR: "₹",
    RUB: "руб.",
  };

  const suffixCurrencies = [
    "SEK",
    "PLN",
    "CNY",
    "JPY",
    "KRW",
    "TWD",
    "RUB",
    "EUR",
    "DKK",
    "CZK",
  ];

  const curWithOutSpace = ["GBP", "AUD", "INR", "USD"];
  const spaceChar = curWithOutSpace.indexOf(apiCurrency) === -1 ? " " : "";

  // Normalize to number and format with locale decimal precision (e.g. 0 → "0.00")
  const numPrice =
    product_price === undefined || product_price === ""
      ? 0
      : parseFloat(String(product_price).replace(/,/g, '').trim());
  const formattedPrice = setPriceFormatToLocale(Number.isNaN(numPrice) ? 0 : numPrice);
  let price = formattedPrice;

  if (suffixCurrencies.indexOf(apiCurrency) !== -1) {
    price = price + spaceChar + dispCurrencyMap[apiCurrency];
  } else {
    price = dispCurrencyMap[apiCurrency] + spaceChar + price;
  }

  return price;
};

/**
 * Export nvCartHelpers object
 */
export const nvCartHelpers = {
  getCartTotalStr,
  getCartTotal,
  getProductCount,
  getTotalProductCount,
  productIsInCart,
  setPriceFormatToLocale,
  localisePriceDisplay,
  getLimitPerCustomer,
  getAvailableLimit,
  productCartProductPrice,
};

// Make nvCartHelpers available globally for legacy compatibility
if (typeof window !== 'undefined') {
  window.site = window.site || {};
  if (window.nvCartHelpers == null) {
    window.nvCartHelpers = nvCartHelpers;
  }
}

export default nvCartHelpers;

